<template>
  <div class="flex space-x-2">
    <div>
      {{
        YES_NO_OPTIONS[
          (Number(Boolean(value)) || 0) as keyof typeof YES_NO_OPTIONS
        ]
      }}
    </div>
    <button
      class="th-arrow-button rounded-md border border-gray-200 bg-white"
      @click="loadEnrichmentData"
    >
      <loader :is-loading="isEnrichmentDataLoading" />
      <icon-base
        :icon="icon"
        class="mt-0.5 transform"
        :viewBox="iconViewBox"
        :width="iconWidth"
        :height="iconHeight"
      />
    </button>
  </div>
</template>

<script lang="ts">
export enum EnrichmentData {
  offer = "offer",
  placement = "placement",
  task = "task",
}

export interface EmitPayload<
  T extends
    | IOffer
    | ILenderPlacement
    | CommunicationLogResponse["data"][number],
> {
  enrichmentData: T[];
  applicationId: IApplication["id"];
}
</script>

<script lang="ts" setup>
import { computed, ref } from "vue";

import { CommunicationType } from "@/enums/communicationLogs";
import { YES_NO_OPTIONS } from "@/helpers/constants";
import { usePromiseWrapper } from "@/hooks/common";
import applicationsApiService from "@/services/modules/applications";
import communicationService from "@/services/modules/communication";

import IconArrowRight from "@/components/icons/IconArrowRight.vue";
import IconReload from "@/components/icons/IconReload.vue";

import type { IApplication } from "@/models/applications";
import type {
  CommunicationFilters,
  CommunicationLogResponse,
} from "@/models/communicationLogs";
import type {
  ILenderPlacement,
  IOffer,
  OffersByField,
  PlacementRecordsByField,
  TasksByField,
} from "@/models/funders";

const props = defineProps<{
  value: unknown;
  type: EnrichmentData;
  model: IApplication & {
    placementData: PlacementRecordsByField;
    offersData: OffersByField;
    tasksData: TasksByField;
  };
}>();

const emit = defineEmits<{
  enrichmentDataLoaded: [
    EmitPayload<
      IOffer | ILenderPlacement | CommunicationLogResponse["data"][number]
    >,
  ];
}>();

const isOpen = ref(false);

const DATA_GETTER = {
  [EnrichmentData.offer]: applicationsApiService.getOffers,
  [EnrichmentData.placement]: applicationsApiService.getPlacements,
  [EnrichmentData.task]: communicationService.getCommunicationList,
};

const hasEnrichmentData = computed(() => {
  const dataMap = {
    [EnrichmentData.offer]: !!props.model.offersData?.funder?.length,
    [EnrichmentData.placement]: !!props.model.placementData?.funder?.length,
    [EnrichmentData.task]: !!props.model.tasksData.assignee?.length,
  };
  return dataMap[props.type];
});

const icon = computed(() => {
  return hasEnrichmentData.value ? IconReload : IconArrowRight;
});

const iconViewBox = computed(() => {
  return hasEnrichmentData.value ? "0 0 24 24" : "";
});

const iconWidth = computed(() => {
  return hasEnrichmentData.value ? 20 : 16;
});

const iconHeight = computed(() => {
  return hasEnrichmentData.value ? 20 : 16;
});

const { fetchWrapper: loadEnrichmentData, loading: isEnrichmentDataLoading } =
  usePromiseWrapper(async () => {
    isOpen.value = true;
    if (props.type === EnrichmentData.task) {
      const params: Partial<CommunicationFilters> = {
        type: CommunicationType.task,
        application_ids: [props.model.id],
      };
      const response: CommunicationLogResponse =
        await communicationService.getCommunicationList(params);
      emit("enrichmentDataLoaded", {
        enrichmentData: response.data,
        applicationId: props.model.id,
      });
      return;
    }

    const data = await DATA_GETTER[props.type](props.model.id);
    emit("enrichmentDataLoaded", {
      enrichmentData: data,
      applicationId: props.model.id,
    });
  });
</script>
